// Child-facing navigation and saved-answer labels derived from the existing
// authored phase, existing completion gates, and saved step state. This does
// not create another progress engine or change any gate/visited state.

const hasRecordedValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.values(value).some(hasRecordedValue);
  return typeof value === "string" ? value.trim().length > 0 : true;
};

// DOM anchor for a rendered activity. Only the active Journey page is mounted.
export const swanActivityDomId = (blockId) =>
  `swan-activity-${encodeURIComponent(String(blockId || ""))}`;

const visibleLabel = (value) => String(value || "").trim().slice(0, 180);

const selectedAnswer = (block, value) => {
  const options = [...(block?.choices || []), ...(block?.options || []), ...(block?.items || [])];
  const labelFor = (id) => options.find((option) => option?.id === id)?.label || id;
  if (Array.isArray(value)) return value.map(labelFor);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, answer]) => [
        labelFor(key),
        (block?.categories || []).find((category) => category.id === answer)?.label || answer,
      ])
    );
  }
  return labelFor(value);
};

const labeledValue = (block, value) => {
  if (value && typeof value === "object" && !Array.isArray(value) && Array.isArray(block?.fields)) {
    return Object.fromEntries(
      block.fields.filter((field) => Object.prototype.hasOwnProperty.call(value, field.id))
        .map((field) => [field.label || field.prompt || field.id,
          (field.choices || []).find((choice) => choice.id === value[field.id])?.label || value[field.id]]));
  }
  if (["choice_cards", "multi_select", "quiz_question", "classification", "matching"].includes(block?.type)) {
    return selectedAnswer(block, value);
  }
  return value;
};

const instructionForBlock = (block, value) => {
  const title = visibleLabel(block?.title) || "this activity";
  const guidance = visibleLabel(block?.self_guided_guidance?.try_now || block?.self_guided_cue);
  switch (block?.type) {
    case "image_reveal":
      return `Click “Reveal the next clue” in “${title}”, then read the revealed clues.`;
    case "image_gallery":
      return `You can inspect “${title}” for extra clues. This exploration is optional.`;
    case "local_video":
      return `I recommend watching “${title}” for clues about the mission. It is not required to continue, and you can explore at your own pace.`;
    case "evidence_response":
      return `Write your own answer in “${title}”. ${visibleLabel(block?.prompt)}`.trim();
    case "experiment_record": {
      const remaining = (block?.fields || []).find((field) => field.required !== false && !hasRecordedValue(value?.[field.id]));
      return remaining
        ? `In “${title}”, record “${visibleLabel(remaining.label || remaining.prompt || remaining.id)}” using your actual observation, not a guess.`
        : `Finish recording your observations in “${title}”.`;
    }
    case "build_checklist": {
      const unchecked = (block?.items || []).find((item) => !value?.[item.id] || value?.[item.id] === "help");
      return unchecked
        ? `In “${title}”, check “${visibleLabel(unchecked.label || unchecked.text || unchecked.id)}” and follow the approved kit instructions.`
        : `Review “${title}” and the checks you recorded.`;
    }
    case "choice_cards":
    case "multi_select":
    case "quiz_question":
    case "classification":
    case "matching":
      return `Make or revise your selection in “${title}”. ${visibleLabel(block?.prompt)}`.trim();
    case "sequence_builder":
      return `Arrange the items in “${title}” using the evidence you've collected.`;
    case "master_teacher_diagnostic":
      return `Tell Swan what you noticed in “${title}”, then report the result of ONE safe check.`;
    case "video_asset":
    case "pwo_asset":
      return `Open “${title}” and explore the supplied ${block?.type === "pwo_asset" ? "3D model" : "video"}.`;
    default:
      return guidance || visibleLabel(block?.prompt || block?.mission) || `Work on “${title}”.`;
  }
};

// Protect unfinished authored evidence questions. Correctness comes from the
// existing completion gate; this is ONLY guidance for Swan, never a new gate.
const EVIDENCE_CHOICE_TYPES = new Set([
  "choice_cards", "quiz_question", "multi_select", "classification",
  "matching", "sequence_builder",
]);

const SKIP = new Set(["completion_gate", "saras_checkpoint", "story_hook", "story_prelude", "prior_work_summary"]);

export const buildSwanActivityContext = (page, stepState = {}, completionState = {}, visibleBlocks = []) => {
  const blocks = Array.isArray(visibleBlocks) ? visibleBlocks : [];
  const required = Array.isArray(completionState?.items) ? completionState.items : [];
  const first = completionState?.firstIncomplete || null;
  const requiredBlock = first?.targetBlockId
    ? blocks.find((block) => block.id === first.targetBlockId)
    : null;
  const requiredIndex = requiredBlock ? blocks.indexOf(requiredBlock) : Infinity;
  const pendingVideo = !completionState?.allComplete && blocks.find((block, i) =>
    i < requiredIndex && block.type === "local_video" && block.save_as && !hasRecordedValue(stepState?.[block.save_as]));
  const target = pendingVideo || requiredBlock;
  const roleFor = (block, gate) => gate
    ? "required"
    : ["local_video", "video_asset", "story_prelude", "saras_checkpoint"].includes(block.type)
      ? "recommended"
      : "optional";
  const progress = blocks.filter((block) => block?.save_as && !SKIP.has(block.type)).map((block) => {
    const gate = required.find((item) => item.field === block.save_as);
    return {
      block_id: block.id,
      type: block.type,
      title: visibleLabel(block.title),
      field: block.save_as,
      required_for_phase: Boolean(gate),
      guidance_role: roleFor(block, gate),
      requirement_complete: gate ? Boolean(gate.complete) : null,
      has_saved_response: hasRecordedValue(stepState?.[block.save_as]),
    };
  });
  // Content guidance, not completion requirements: a video may be recommended
  // without ever creating a new gate. Opening the Swan case is not tracked, so
  // never claim it was or wasn't opened.
  const exploration = blocks.filter((block) =>
    ["local_video", "video_asset", "story_prelude", "saras_checkpoint", "image_gallery"].includes(block.type)
  ).map((block) => ({
    block_id: block.id,
    title: visibleLabel(block.title),
    type: block.type,
    guidance_role: roleFor(block, required.find((item) => item.field === block.save_as)),
    playback_end_recorded: block.type === "local_video" && !!block.save_as
      ? Boolean(stepState[block.save_as]) : null,
    opened_recorded: block.type === "video_asset" && !!block.save_as
      ? Boolean(stepState[block.save_as]) : null,
    visited: null, // Do not infer watching or opened cases from an untracked action.
  }));
  const responses = blocks.filter((block) => block?.save_as && hasRecordedValue(stepState?.[block.save_as]))
    .slice(0, 24).map((block) => ({
      block_id: block.id,
      question: visibleLabel(block.prompt || block.title),
      field: block.save_as,
      // Learner data, not evidence of an actual physical result.
      response_kind: block.master_teacher_response_kind ||
        (block.type === "experiment_record" ? "child_reported_observation" :
        block.type === "choice_cards" ? "learner_choice_not_proof" : "saved_response"),
      verification_level: "child_reported_or_selected_not_verified_by_ai",
      recorded_answer: labeledValue(block, stepState[block.save_as]),
    }));

  let instruction;
  let targetBlock = target;
  if (target) {
    instruction = instructionForBlock(target, stepState?.[target.save_as]);
  } else if (completionState?.allComplete) {
    const optionalGallery = blocks.find((block) => block.type === "image_gallery" &&
      block.save_as && !hasRecordedValue(stepState?.[block.save_as]));
    targetBlock = optionalGallery || null;
    instruction = optionalGallery
      ? `You have completed the required work. You may inspect “${visibleLabel(optionalGallery.title)}” for more evidence, or click Continue to review this phase and move to the next one.`
      : "You have completed this phase's required work. Click Continue to review it and move to the next phase when ready.";
  } else {
    instruction = visibleLabel(page?.self_guided?.do_now) ||
      "Look at the current activity, complete its instructions, then ask Professor Swan for help if needed.";
  }

  return {
    phase_complete: Boolean(completionState?.allComplete),
    phase_gate: {
      completed: completionState?.completed || 0,
      total: completionState?.total || 0,
      requirements: required.map((item) => ({
        label: visibleLabel(item.label), field: item.field, complete: Boolean(item.complete),
      })),
    },
    next_action: {
      instruction,
      block_id: targetBlock?.id || null,
      block_type: targetBlock?.type || null,
      title: targetBlock?.title || null,
      required_for_phase: Boolean(target && target === requiredBlock),
    },
    activities: progress.slice(0, 40),
    exploration_opportunities: exploration.slice(0, 12),
    recorded_responses: responses,
    report_clarification: page?.step_id === "fdr-apply" &&
      stepState?.motion_test?.motion === "moved" &&
      stepState?.motion_test?.wheel_contact === "spun"
      ? "Child reported both travel and spinning-in-place. Ask whether these occurred at different moments; do not assume the whole trial succeeded or failed."
      : null,
    // A wrong/unfinished selection is a chance to inspect evidence, NOT an
    // invitation for AI to supply the correct option. No answer key is sent.
    inquiry_guard: !completionState?.allComplete && requiredBlock &&
      EVIDENCE_CHOICE_TYPES.has(requiredBlock.type)
      ? {
          protect_answer: true,
          block_id: requiredBlock.id,
          activity_title: visibleLabel(requiredBlock.title),
          learner_question: visibleLabel(requiredBlock.prompt),
          learner_has_selected: hasRecordedValue(stepState?.[requiredBlock.save_as]),
          // Only the child-visible options, never the correct answer key.
          // Used by the client-side response safeguard if the model slips.
          choice_labels: (requiredBlock.choices || requiredBlock.options || [])
            .map((item) => visibleLabel(item?.label)).filter(Boolean),
          safe_hint: visibleLabel(
            requiredBlock.self_guided_incorrect_feedback ||
            "Look at the evidence in the current model or activity. What can you observe before choosing?"
          ),
          // Optional authored ways to investigate more deeply, not answers or
          // extra completion requirements. The response guard uses these only
          // when a model reply leaks a choice or repeats earlier advice.
          hint_steps: Array.isArray(requiredBlock.self_guided_hint_steps)
            ? requiredBlock.self_guided_hint_steps.map(visibleLabel).filter(Boolean).slice(0, 4)
            : [],
        }
      : null,
    // Story and phase notes are read, not asserted as completed.
    phase_guidance: visibleLabel(page?.self_guided?.do_now),
  };
};

// Presentation navigation is UI state, not a completion rule. Inject it only
// at Ask time so Swan names the button the learner can see RIGHT NOW.
export const withSwanNavigation = (activityProgress, navigation) => {
  if (!activityProgress || !navigation) return activityProgress;
  const label = String(navigation.label || "").trim();
  const result = {
    ...activityProgress,
    navigation: {
      visible_button: label,
      enabled: !navigation.disabled,
      stage: navigation.stage,
    },
  };
  if (!activityProgress.phase_complete) return result;
  const optional = activityProgress.next_action?.block_id
    ? `You may also explore “${visibleLabel(activityProgress.next_action.title)}” for extra clues. `
    : "";
  let instruction = `You've finished the required work in this phase. ${optional}`;
  if (navigation.stage === "reviewing") {
    instruction += "Professor Swan is reviewing this phase; the navigation button is temporarily disabled.";
  } else if (navigation.stage === "advance") {
    instruction += `When you are ready, click “${label}” to move on.`;
  } else {
    instruction += `Click “${label}” to review this phase with Professor Swan before moving on.`;
  }
  return {
    ...result,
    next_action: { ...activityProgress.next_action, instruction: instruction.trim() },
  };
};

// Same labels/disabled logic as the presentation footer; not a new workflow.
export const getSwanNavigationState = ({
  isSelfGuidedFocus, atEnd, pageCompletion, pendingTransition, index,
}) => {
  const blocked = Boolean(isSelfGuidedFocus && !atEnd &&
    (pageCompletion === null ||
      (pageCompletion?.hasGate && !pageCompletion.allComplete)));
  const transitionHere = pendingTransition?.fromIndex === index;
  const reviewing = transitionHere && pendingTransition.status === "loading";
  return {
    label: blocked
      ? "Finish this step"
      : transitionHere
        ? (reviewing ? "Reviewing…" :
          atEnd ? "Finish Adventure" : "Next phase")
        : atEnd
          ? (isSelfGuidedFocus && pageCompletion?.allComplete ? "Review this phase" : "End of Journey")
          : isSelfGuidedFocus && pageCompletion?.allComplete
            ? "Review this phase"
            : isSelfGuidedFocus ? "Continue" : "Next",
    disabled: blocked || pendingTransition?.status === "loading",
    stage: reviewing ? "reviewing" : transitionHere ? "advance" : "review",
  };
};
